import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Image as ImageIcon,
  Wand2,
  RefreshCw,
  Trash2,
  Eye,
  Download,
  Maximize2,
  Check,
  Cpu,
  Sparkles,
} from 'lucide-react';
import {
  ModuleHeader,
  GlassCard,
  GlassTabs,
  GlassButton,
  GlassSelect,
  GlassBadge,
  GlassTable,
  GlassModal,
} from '../ui';
import {
  generateImage,
  resizeImage,
  getImages,
  getImageFull,
  deleteImage,
  generateImagePrompt,
  GeneratedImageMeta,
  GeneratedImageFull,
} from '../../services/api';
import ImageGenerationPanel from './ImageGenerationPanel';
import './Images.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


// ── Platform size reference data ──────────────────────────────────────────────
const PLATFORM_SIZES: { platform: string; width: number; height: number; use: string }[] = [
  { platform: 'linkedin',          width: 1200, height: 627,  use: 'LinkedIn post / article cover' },
  { platform: 'instagram',         width: 1080, height: 1080, use: 'Instagram square post' },
  { platform: 'instagram_story',   width: 1080, height: 1920, use: 'Instagram / Facebook Story' },
  { platform: 'twitter',           width: 1200, height: 675,  use: 'Twitter / X post card' },
  { platform: 'facebook',          width: 1200, height: 630,  use: 'Facebook post' },
  { platform: 'og',                width: 1200, height: 630,  use: 'Open Graph (link preview)' },
  { platform: 'youtube_thumbnail', width: 1280, height: 720,  use: 'YouTube video thumbnail' },
  { platform: 'linkedin_banner',   width: 1584, height: 396,  use: 'LinkedIn profile/company banner' },
];

const STYLE_OPTIONS = [
  { value: '', label: 'No specific style' },
  { value: 'Photo-realistic', label: 'Photo-realistic' },
  { value: 'Illustration', label: 'Illustration' },
  { value: 'Minimalist', label: 'Minimalist' },
  { value: 'Abstract', label: 'Abstract' },
  { value: 'Professional', label: 'Professional' },
  { value: 'Corporate', label: 'Corporate' },
];

const ASSET_TYPE_OPTIONS = [
  { value: '', label: 'No asset type' },
  { value: 'hero', label: 'Hero Image' },
  { value: 'social_post', label: 'Social Post' },
  { value: 'blog_header', label: 'Blog Header' },
  { value: 'ad_banner', label: 'Ad Banner' },
  { value: 'thumbnail', label: 'Thumbnail' },
];

const ASSET_TYPE_LABELS: Record<string, string> = {
  hero: 'Hero Image',
  social_post: 'Social Post',
  blog_header: 'Blog Header',
  ad_banner: 'Ad Banner',
  thumbnail: 'Thumbnail',
};

// ── Helper ────────────────────────────────────────────────────────────────────
function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function platformLabel(p: string): string {
  return p.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

// ── Component ─────────────────────────────────────────────────────────────────
const ImagesModule: React.FC = () => {
  const ctx = useOutletContext<{ selectedProjectId?: string }>();
  const selectedProjectId = ctx?.selectedProjectId;

  const [activeTab, setActiveTab] = useState('generate');

  // Generate tab state
  const [prompt, setPrompt] = useState('');
  const [style, setStyle] = useState('');
  const [assetType, setAssetType] = useState('');
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState('');
  const [generatedResult, setGeneratedResult] = useState<{
    image_id: string;
    image_base64: string;
    width: number;
    height: number;
    revised_prompt?: string;
    noise_applied?: boolean;
  } | null>(null);

  // Prompt helper
  const [promptHelping, setPromptHelping] = useState(false);
  const [styleSuggestions, setStyleSuggestions] = useState<string[]>([]);

  // Resize state
  const [selectedPlatforms, setSelectedPlatforms] = useState<string[]>([]);
  const [resizing, setResizing] = useState(false);
  const [resizeError, setResizeError] = useState('');
  const [variants, setVariants] = useState<Record<string, string>>({});

  // Library tab state
  const [images, setImages] = useState<GeneratedImageMeta[]>([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryError, setLibraryError] = useState('');
  const [filterAssetType, setFilterAssetType] = useState('');

  // View modal state
  const [viewingImage, setViewingImage] = useState<GeneratedImageFull | null>(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);

  // Delete confirm
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // ── Load library ──────────────────────────────────────────────────────────
  const loadLibrary = useCallback(async () => {
    setLibraryLoading(true);
    setLibraryError('');
    try {
      const data = await getImages(selectedProjectId ? { project_id: selectedProjectId } : undefined);
      setImages(data);
    } catch (e: any) {
      setLibraryError(getApiError(e, 'Failed to load images'));
    } finally {
      setLibraryLoading(false);
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (activeTab === 'library') {
      loadLibrary();
    }
  }, [activeTab, loadLibrary, selectedProjectId]);

  // ── Generate ─────────────────────────────────────────────────────────────
  const handleGenerate = async () => {
    if (!prompt.trim()) return;
    setGenerating(true);
    setGenerateError('');
    setGeneratedResult(null);
    setVariants({});
    setSelectedPlatforms([]);
    try {
      const result = await generateImage({
        prompt: prompt.trim(),
        style: style || undefined,
        asset_type: assetType || undefined,
      });
      setGeneratedResult(result);
    } catch (e: any) {
      setGenerateError(getApiError(e, 'Image generation failed'));
    } finally {
      setGenerating(false);
    }
  };

  // ── Prompt helper ─────────────────────────────────────────────────────────
  const handlePromptHelp = async () => {
    if (!assetType) {
      setGenerateError('Select an asset type first to get prompt suggestions');
      return;
    }
    setPromptHelping(true);
    setGenerateError('');
    try {
      const result = await generateImagePrompt({
        context: prompt || `Marketing image for ${ASSET_TYPE_LABELS[assetType] || assetType}`,
        asset_type: assetType,
      });
      setPrompt(result.prompt);
      setStyleSuggestions(result.style_suggestions || []);
    } catch (e: any) {
      setGenerateError(getApiError(e, 'Prompt generation failed'));
    } finally {
      setPromptHelping(false);
    }
  };

  // ── Resize ────────────────────────────────────────────────────────────────
  const handleResize = async () => {
    if (!generatedResult || selectedPlatforms.length === 0) return;
    setResizing(true);
    setResizeError('');
    try {
      const result = await resizeImage(generatedResult.image_id, selectedPlatforms);
      setVariants(result.variants || {});
    } catch (e: any) {
      setResizeError(getApiError(e, 'Resize failed'));
    } finally {
      setResizing(false);
    }
  };

  const togglePlatform = (p: string) => {
    setSelectedPlatforms(prev =>
      prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
    );
  };

  // ── View image ────────────────────────────────────────────────────────────
  const handleViewImage = async (imageId: string) => {
    setViewLoading(true);
    setViewModalOpen(true);
    try {
      const full = await getImageFull(imageId);
      setViewingImage(full);
    } catch {
      setViewingImage(null);
    } finally {
      setViewLoading(false);
    }
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDeleteConfirm = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await deleteImage(deleteId);
      setDeleteModalOpen(false);
      setDeleteId(null);
      loadLibrary();
    } catch {
      // silent
    } finally {
      setDeleting(false);
    }
  };

  // ── Download helper ───────────────────────────────────────────────────────
  const downloadBase64 = (b64: string, filename: string) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${b64}`;
    link.download = filename;
    link.click();
  };

  // ── Filtered library ──────────────────────────────────────────────────────
  const filteredImages = filterAssetType
    ? images.filter(i => i.asset_type === filterAssetType)
    : images;

  // ── Tabs ──────────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'generate',   label: 'Generate (Gemini)' },
    { id: 'ai_models',  label: 'AI Models' },
    { id: 'library',    label: 'Library' },
    { id: 'sizes',      label: 'Platform Sizes' },
  ];

  if (!selectedProjectId) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--text-secondary)' }}>
        <ImageIcon size={64} style={{ opacity: 0.4, marginBottom: '16px' }} />
        <h3 style={{ color: 'var(--text)', marginBottom: '8px' }}>Select a project</h3>
        <p>Choose a project from the sidebar to view its images</p>
      </div>
    );
  }

  return (
    <div className="images-module">
      <ModuleHeader
        title="Images"
        breadcrumb={['Content', 'Images']}
        actions={[]}
      />

      <div className="images-tabs-row">
        <GlassTabs tabs={tabs} activeTab={activeTab} onChange={setActiveTab} />
      </div>

      {/* ── GENERATE TAB ── */}
      {activeTab === 'generate' && (
        <div className="images-generate-layout">
          {/* Left: form */}
          <GlassCard>
            <div className="images-form-section">
              <div className="images-prompt-field">
                <label className="images-prompt-label">Image Prompt</label>
                <textarea
                  className="images-prompt-textarea"
                  placeholder="Describe the image you want to generate…"
                  value={prompt}
                  onChange={e => setPrompt(e.target.value)}
                  rows={5}
                />
                <div className="images-prompt-help-btn">
                  <GlassButton
                    variant="secondary"
                    size="sm"
                    icon={<Wand2 size={14} />}
                    onClick={handlePromptHelp}
                    disabled={promptHelping}
                  >
                    {promptHelping ? 'Writing…' : 'Help me write a prompt'}
                  </GlassButton>
                </div>
                {styleSuggestions.length > 0 && (
                  <div className="images-platform-grid" style={{ marginTop: 'var(--space-2)' }}>
                    {styleSuggestions.map(s => (
                      <span
                        key={s}
                        className={`images-platform-chip${style === s ? ' selected' : ''}`}
                        onClick={() => setStyle(s)}
                      >
                        {style === s && <Check size={12} />}
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="images-form-row">
                <GlassSelect
                  label="Style"
                  options={STYLE_OPTIONS}
                  value={style}
                  onChange={setStyle}
                  placeholder="Any style"
                />
                <GlassSelect
                  label="Asset Type"
                  options={ASSET_TYPE_OPTIONS}
                  value={assetType}
                  onChange={setAssetType}
                  placeholder="Any type"
                />
              </div>

              {generateError && (
                <div className="images-error-msg">{generateError}</div>
              )}

              <GlassButton
                variant="primary"
                icon={<ImageIcon size={16} />}
                onClick={handleGenerate}
                disabled={generating || !prompt.trim()}
                style={{ width: '100%' }}
              >
                {generating ? 'Generating image with AI…' : 'Generate Image'}
              </GlassButton>
            </div>
          </GlassCard>

          {/* Right: result */}
          <div className="images-result-section">
            <GlassCard>
              {generating && (
                <div className="images-loading">
                  <div className="images-spinner" />
                  Generating image with AI…
                </div>
              )}

              {!generating && !generatedResult && (
                <div className="images-result-placeholder">
                  <ImageIcon size={48} strokeWidth={1} />
                  <span>Your generated image will appear here</span>
                </div>
              )}

              {!generating && generatedResult && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                  <div className="images-result-img-wrap">
                    <img
                      className="images-result-img"
                      src={`data:image/png;base64,${generatedResult.image_base64}`}
                      alt="Generated"
                    />
                  </div>
                  <div className="images-result-meta">
                    <GlassBadge variant="info">{generatedResult.width} × {generatedResult.height}</GlassBadge>
                    {assetType && <GlassBadge variant="default">{ASSET_TYPE_LABELS[assetType] || assetType}</GlassBadge>}
                    {generatedResult.noise_applied && (
                      <GlassBadge variant="info">
                        <Sparkles size={11} style={{ marginRight: 4 }} />
                        Noise filter applied
                      </GlassBadge>
                    )}
                    <GlassButton
                      variant="ghost"
                      size="sm"
                      icon={<Download size={14} />}
                      onClick={() => downloadBase64(generatedResult.image_base64, 'generated-image.png')}
                    >
                      Download
                    </GlassButton>
                  </div>
                  {generatedResult.revised_prompt && (
                    <div className="images-revised-prompt">
                      Revised prompt: {generatedResult.revised_prompt}
                    </div>
                  )}
                </div>
              )}
            </GlassCard>

            {/* Resize section — shown only after generation */}
            {generatedResult && (
              <GlassCard>
                <div className="images-resize-section">
                  <p className="images-section-title">Resize for Platforms</p>
                  <div className="images-platform-grid">
                    {PLATFORM_SIZES.map(p => (
                      <span
                        key={p.platform}
                        className={`images-platform-chip${selectedPlatforms.includes(p.platform) ? ' selected' : ''}`}
                        onClick={() => togglePlatform(p.platform)}
                        title={`${p.width}×${p.height}`}
                      >
                        {selectedPlatforms.includes(p.platform) && <Check size={12} />}
                        {platformLabel(p.platform)}
                      </span>
                    ))}
                  </div>

                  {resizeError && (
                    <div className="images-error-msg">{resizeError}</div>
                  )}

                  <GlassButton
                    variant="secondary"
                    icon={<Maximize2 size={14} />}
                    onClick={handleResize}
                    disabled={resizing || selectedPlatforms.length === 0}
                  >
                    {resizing ? 'Resizing…' : `Resize for ${selectedPlatforms.length || 0} platform${selectedPlatforms.length !== 1 ? 's' : ''}`}
                  </GlassButton>

                  {Object.keys(variants).length > 0 && (
                    <>
                      <p className="images-section-title" style={{ marginTop: 'var(--space-2)' }}>
                        Platform Variants
                      </p>
                      <div className="images-variants-grid">
                        {Object.entries(variants).map(([platform, b64]) => {
                          const dims = PLATFORM_SIZES.find(p => p.platform === platform);
                          return (
                            <div key={platform} className="images-variant-card">
                              <img
                                className="images-variant-img"
                                src={`data:image/png;base64,${b64}`}
                                alt={platform}
                              />
                              <span className="images-variant-label">{platformLabel(platform)}</span>
                              {dims && (
                                <span className="images-variant-dims">{dims.width}×{dims.height}</span>
                              )}
                              <GlassButton
                                variant="ghost"
                                size="sm"
                                icon={<Download size={12} />}
                                onClick={() => downloadBase64(b64, `${platform}.png`)}
                              >
                                Download
                              </GlassButton>
                            </div>
                          );
                        })}
                      </div>
                    </>
                  )}
                </div>
              </GlassCard>
            )}
          </div>
        </div>
      )}

      {/* ── AI MODELS TAB (Sprint 2.2) ── */}
      {activeTab === 'ai_models' && (
        <ImageGenerationPanel />
      )}

      {/* ── LIBRARY TAB ── */}
      {activeTab === 'library' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <GlassCard>
            <div className="images-library-filters">
              <GlassSelect
                label="Filter by type"
                options={[
                  { value: '', label: 'All types' },
                  ...ASSET_TYPE_OPTIONS.filter(o => o.value),
                ]}
                value={filterAssetType}
                onChange={setFilterAssetType}
                placeholder="All types"
              />
              <GlassButton
                variant="secondary"
                size="sm"
                icon={<RefreshCw size={14} />}
                onClick={loadLibrary}
                disabled={libraryLoading}
              >
                Refresh
              </GlassButton>
            </div>
          </GlassCard>

          {libraryLoading && (
            <div className="images-loading">
              <div className="images-spinner" />
              Loading images…
            </div>
          )}

          {!libraryLoading && libraryError && (
            <div className="images-error-msg">{libraryError}</div>
          )}

          {!libraryLoading && !libraryError && filteredImages.length === 0 && (
            <GlassCard>
              <div className="images-library-empty">
                <ImageIcon size={48} strokeWidth={1} />
                <span>No images generated yet. Head to the Generate tab to create your first image.</span>
              </div>
            </GlassCard>
          )}

          {!libraryLoading && filteredImages.length > 0 && (
            <div className="images-library-grid">
              {filteredImages.map(img => (
                <GlassCard key={img.image_id} className="images-library-card">
                  <div
                    className="images-library-thumb-wrap"
                    onClick={() => handleViewImage(img.image_id)}
                  >
                    <div className="images-library-thumb-placeholder">
                      <ImageIcon size={32} strokeWidth={1} />
                    </div>
                    <div className="images-library-thumb-overlay">
                      <Eye size={16} />
                      View
                    </div>
                  </div>

                  <div className="images-library-meta">
                    <span className="images-library-prompt" title={img.prompt}>
                      {img.prompt}
                    </span>
                    <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap', alignItems: 'center' }}>
                      {img.asset_type && (
                        <GlassBadge variant="info">
                          {ASSET_TYPE_LABELS[img.asset_type] || img.asset_type}
                        </GlassBadge>
                      )}
                      {img.has_variants && (
                        <GlassBadge variant="success">Has variants</GlassBadge>
                      )}
                    </div>
                    <span className="images-library-sub">
                      {img.width && img.height ? `${img.width}×${img.height} · ` : ''}
                      {formatDate(img.created_at)}
                    </span>
                  </div>

                  <div className="images-library-actions">
                    <GlassButton
                      variant="secondary"
                      size="sm"
                      icon={<Eye size={14} />}
                      onClick={() => handleViewImage(img.image_id)}
                    >
                      View
                    </GlassButton>
                    <GlassButton
                      variant="danger"
                      size="sm"
                      icon={<Trash2 size={14} />}
                      onClick={() => { setDeleteId(img.image_id); setDeleteModalOpen(true); }}
                    >
                      Delete
                    </GlassButton>
                  </div>
                </GlassCard>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── PLATFORM SIZES TAB ── */}
      {activeTab === 'sizes' && (
        <GlassCard>
          <p className="images-section-title" style={{ marginBottom: 'var(--space-4)' }}>
            Supported Platform Dimensions
          </p>
          <div className="images-sizes-table-wrap">
            <GlassTable
              columns={[
                { key: 'platform', label: 'Platform', width: '30%' },
                { key: 'dimensions', label: 'Dimensions (px)', width: '20%' },
                { key: 'use', label: 'Recommended Use', width: '50%' },
              ]}
              data={PLATFORM_SIZES.map(p => ({
                platform: platformLabel(p.platform),
                dimensions: `${p.width} × ${p.height}`,
                use: p.use,
                _key: p.platform,
              }))}
              rowKey="_key"
              emptyMessage="No platform sizes defined"
            />
          </div>
        </GlassCard>
      )}

      {/* ── VIEW IMAGE MODAL ── */}
      <GlassModal
        isOpen={viewModalOpen}
        onClose={() => { setViewModalOpen(false); setViewingImage(null); }}
        title="Image Preview"
        size="lg"
      >
        {viewLoading && (
          <div className="images-loading">
            <div className="images-spinner" />
            Loading…
          </div>
        )}
        {!viewLoading && viewingImage && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <img
              className="images-modal-img"
              src={`data:image/png;base64,${viewingImage.original_image}`}
              alt={viewingImage.prompt}
            />
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'center' }}>
              <GlassBadge variant="info">
                {viewingImage.width} × {viewingImage.height}
              </GlassBadge>
              {viewingImage.asset_type && (
                <GlassBadge variant="default">
                  {ASSET_TYPE_LABELS[viewingImage.asset_type] || viewingImage.asset_type}
                </GlassBadge>
              )}
              <GlassButton
                variant="secondary"
                size="sm"
                icon={<Download size={14} />}
                onClick={() => downloadBase64(viewingImage.original_image, 'image.png')}
              >
                Download Original
              </GlassButton>
            </div>

            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)', margin: 0 }}>
              {viewingImage.prompt}
            </p>

            {Object.keys(viewingImage.platform_variants || {}).length > 0 && (
              <>
                <p className="images-section-title">Platform Variants</p>
                <div className="images-modal-variants">
                  {Object.entries(viewingImage.platform_variants).map(([platform, b64]) => {
                    const dims = PLATFORM_SIZES.find(p => p.platform === platform);
                    return (
                      <div key={platform} className="images-modal-variant-item">
                        <img
                          className="images-modal-variant-img"
                          src={`data:image/png;base64,${b64}`}
                          alt={platform}
                        />
                        <span className="images-modal-variant-label">
                          {platformLabel(platform)}
                          {dims ? ` (${dims.width}×${dims.height})` : ''}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </GlassModal>

      {/* ── DELETE CONFIRM MODAL ── */}
      <GlassModal
        isOpen={deleteModalOpen}
        onClose={() => { setDeleteModalOpen(false); setDeleteId(null); }}
        title="Delete Image"
        size="sm"
        footer={
          <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
            <GlassButton
              variant="secondary"
              onClick={() => { setDeleteModalOpen(false); setDeleteId(null); }}
            >
              Cancel
            </GlassButton>
            <GlassButton
              variant="danger"
              icon={<Trash2 size={14} />}
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </GlassButton>
          </div>
        }
      >
        <p style={{ color: 'var(--text)', margin: 0 }}>
          Are you sure you want to delete this image? This action cannot be undone.
        </p>
      </GlassModal>
    </div>
  );
};

export default ImagesModule;
